import { createTheme, ThemeProvider } from '@mui/material';
import { ColorPartial } from '@mui/material/styles/createPalette';

// Override fontfamily on Typography and TextField
const theme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        root: {
          fontFamily: 'Schibsted Grotesk, sans-serif',
        },
      },
    },
  },
  typography: {
    fontFamily: 'Schibsted Grotesk, sans-serif',
  },
});

//Addition of custom colors
const extendedTheme = createTheme(theme, {
  palette: {
    teal: theme.palette.augmentColor({
      color: {
        main: '#00CECF',
      },
      name: 'teal',
    }),
    salmon: theme.palette.augmentColor({
      color: {
        main: '#FFBCB9',
      },
      name: 'salmon',
    }),
    deepSalmon: theme.palette.augmentColor({
      color: {
        main: '#FF5E56',
      },
      name: 'deepSalmon',
    }),
    lightGray: theme.palette.augmentColor({
      color: {
        main: '#E0E0E0',
      },
      name: 'lightGray',
    }),
    pink: theme.palette.augmentColor({
      color: {
        main: '#fce4ec',
      },
      name: 'pink',
    }),
    blue: theme.palette.augmentColor({
      color: {
        main: '#2449FC',
      },
    }),
    darkGray: theme.palette.augmentColor({
      color: {
        main: '#878787',
      },
    }),
    neutralGray: theme.palette.augmentColor({
      color: {
        main: '#454545',
      },
    }),
    neutralBlackBg: theme.palette.augmentColor({
      color: {
        50: '#F7F7F7',
        100: '#F0F0F0',
        200: '#E3E3E3',
        300: '#D1D1D1',
        400: '#C0C0C0',
        500: '#AAAAAA',
        600: '#969696',
        700: '#818181',
        800: '#6A6A6A',
        900: '#585858',
      },
    }),
    neutralWhiteBg: theme.palette.augmentColor({
      color: {
        50: '#F6F6F6',
        100: '#E7E7E7',
        200: '#D1D1D1',
        300: '#B0B0B0',
        400: '#878787',
        500: '#6D6D6D',
        600: '#5D5D5D',
        700: '#4F4F4F',
        800: '#454545',
        900: '#3D3D3D',
      },
    }),
    green: theme.palette.augmentColor({
      color: {
        50: '#EDFFF5',
        100: '#D5FFE9',
        200: '#AEFFD3',
        300: '#70FFB2',
        400: '#2BFD8A',
        500: '#03FF75',
        600: '#00C052',
        700: '#009644',
        800: '#067539',
        900: '#076032',
      },
    }),
    red: theme.palette.augmentColor({
      color: {
        50: '#FFF1F1',
        100: '#FFE1E1',
        200: '#FFC7C7',
        300: '#FFA0A0',
        400: '#FF7070',
        500: '#F83B3B',
        600: '#E51D1D',
        700: '#C11414',
        800: '#A01414',
        900: '#841818',
      },
    }),
  },
});

declare module '@mui/material/styles' {
  interface PaletteColor extends ColorPartial { }

  interface Palette {
    teal: Palette['primary'];
    salmon: Palette['primary'];
    deepSalmon: Palette['primary'];
    lightGray: Palette['primary'];
    pink: Palette['primary'];
    blue: Palette['primary'];
    darkGray: Palette['primary'];
    neutralGray: Palette['primary'];
    neutralBlackBg: Palette['primary'];
    neutralWhiteBg: Palette['primary'];
    green: Palette['primary'];
    red: Palette['primary'];
  }

  interface PaletteOptions {
    teal?: PaletteOptions['primary'];
    salmon?: Palette['primary'];
    deepSalmon?: Palette['primary'];
    lightGray?: Palette['primary'];
    pink?: Palette['primary'];
    blue?: Palette['primary'];
    darkGray?: Palette['primary'];
    neutralGray?: Palette['primary'];
    neutralBlackBg?: Palette['primary'];
    neutralWhiteBg?: Palette['primary'];
    green?: Palette['primary'];
    red?: Palette['primary'];
  }
}

// Add custom color option to Toggle Buttons
declare module '@mui/material/ToggleButton' {
  interface ToggleButtonPropsColorOverrides {
    teal: true;
    salmon: true;
    deepSalmon: true;
    lightGray: true;
    pink: true;
    blue: true;
    darkGray: true;
    neutralGray: true;
  }
}

// Add custom color option to TextFields
declare module '@mui/material/TextField' {
  interface TextFieldPropsColorOverrides {
    teal: true;
    salmon: true;
    deepSalmon: true;
    lightGray: true;
    pink: true;
    blue: true;
    darkGray: true;
    neutralGray: true;
  }
}

// Add custom color option to Buttons
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    teal: true;
    salmon: true;
    deepSalmon: true;
    lightGray: true;
    pink: true;
    blue: true;
    darkGray: true;
    neutralGray: true;
  }
}

export { extendedTheme as legacyTheme };
export function LegacyThemeProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  return <ThemeProvider theme={extendedTheme}>{children}</ThemeProvider>;
}
